import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  PRIMARY_MEMBER_DEFAULT_NAME,
  cartLineId,
  createDefaultMember,
  defaultDelivery,
  migrateFlowState,
  newMemberId,
  renamePrimaryMember,
  resetPrimaryMemberName,
} from 'utils/household';
import { exchangeAuthToken } from 'components/auth/AuthActions';
import { getLineUnitPrice } from 'utils/cartPricing';
import { buildCartLine, clearAdjustmentHistory, duplicateCartLine, relineForMember } from 'utils/cartLine';
import { slotKey } from 'utils/deliverySlots';

const STORAGE_KEY = 'sf_public_flow_v2';
const SESSION_KEY = 'sf_public_flow_session';
const AUTH_NAME_KEY = 'sf_public_flow_auth_name';

// Read once at module load rather than inside loadState, so a double-invoked
// state initializer can't consume the flag and skip the reset.
const IS_NEW_PAGE_SESSION = (() => {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch {
    // No sessionStorage (private mode, blocked storage) — leave the stored name
    // alone rather than resetting it on every render pass.
    return false;
  }
})();

const readAuthName = () => {
  try {
    return sessionStorage.getItem(AUTH_NAME_KEY) || '';
  } catch {
    return '';
  }
};

const writeAuthName = (name) => {
  try {
    if (name) sessionStorage.setItem(AUTH_NAME_KEY, name);
    else sessionStorage.removeItem(AUTH_NAME_KEY);
  } catch {
    /* ignore */
  }
};

function loadState() {
  let state;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    state = migrateFlowState(raw ? JSON.parse(raw) : null);
  } catch {
    state = migrateFlowState(null);
  }
  return IS_NEW_PAGE_SESSION ? resetPrimaryMemberName(state) : state;
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

const FlowContext = createContext(null);

export function FlowProvider({ children }) {
  const [state, setState] = useState(loadState);
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    saveState(state);
  }, [state]);

  // Signed in: the primary tab takes the account's full name from the Client table.
  // With no Client row yet it stays "Me" and checkout falls back to the delivery name.
  useEffect(() => {
    if (authLoading) return;
    const previous = readAuthName();

    if (!isAuthenticated) {
      if (!previous) return;
      writeAuthName('');
      setState((prev) => renamePrimaryMember(prev, previous, PRIMARY_MEMBER_DEFAULT_NAME));
      return;
    }

    let cancelled = false;
    (async () => {
      let name = '';
      try {
        const result = await exchangeAuthToken(await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: 'openid profile email offline_access',
          },
        }));
        name = result.ok ? result.fullName : '';
      } catch {
        return;
      }
      if (cancelled || !name || name === previous) return;
      writeAuthName(name);
      setState((prev) => renamePrimaryMember(
        previous ? renamePrimaryMember(prev, previous, PRIMARY_MEMBER_DEFAULT_NAME) : prev,
        PRIMARY_MEMBER_DEFAULT_NAME,
        name,
      ));
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  // Memoised: the `|| []` fallback is a fresh array each render, churning every dependent hook.
  const householdMembers = useMemo(() => state.householdMembers || [], [state.householdMembers]);
  const activeMemberId = state.activeMemberId || householdMembers[0]?.id;

  const activeMember = useMemo(
    () => householdMembers.find((m) => m.id === activeMemberId) || householdMembers[0],
    [householdMembers, activeMemberId],
  );

  const startQuizFlow = useCallback(() => {
    setState((prev) => ({ ...prev, entryFlow: 'quiz-first' }));
  }, []);

  const startMenuFlow = useCallback(() => {
    setState((prev) => ({ ...prev, entryFlow: 'menu-first' }));
  }, []);

  const setActiveMember = useCallback((memberId) => {
    setState((prev) => ({ ...prev, activeMemberId: memberId }));
  }, []);

  const addHouseholdMember = useCallback(({ name, restrictionTags = [], restrictionLabels = {}, customExclusionsText = '', email = '' }) => {
    const member = createDefaultMember({
      id: newMemberId(),
      name: name.trim() || 'Family member',
      restrictionTags,
      restrictionLabels,
      customExclusionsText,
      email,
    });
    setState((prev) => ({
      ...prev,
      householdMembers: [...prev.householdMembers, member],
      activeMemberId: member.id,
    }));
    return member.id;
  }, []);

  const updateHouseholdMember = useCallback((memberId, updates) => {
    setState((prev) => ({
      ...prev,
      householdMembers: prev.householdMembers.map((m) =>
        m.id === memberId ? { ...m, ...updates } : m,
      ),
    }));
  }, []);

  const removeHouseholdMember = useCallback((memberId) => {
    setState((prev) => {
      if (prev.householdMembers.length <= 1) return prev;
      const householdMembers = prev.householdMembers.filter((m) => m.id !== memberId);
      const cart = prev.cart.filter((line) => line.memberId !== memberId);
      const activeMemberId =
        prev.activeMemberId === memberId ? householdMembers[0].id : prev.activeMemberId;
      return { ...prev, householdMembers, cart, activeMemberId };
    });
  }, []);

  /** Quiz completes for the active member (usually "Me") */
  const completeQuizForMember = useCallback((memberId, result) => {
    setState((prev) => ({
      ...prev,
      householdMembers: prev.householdMembers.map((m) =>
        m.id === memberId
          ? {
              ...m,
              hasFullQuiz: true,
              // Carried on the member so a results-page refresh still knows why
              // the profile came back null, and which message goes with it.
              hideNutrition: Boolean(result.hideNutrition),
              hasNutritionist: Boolean(result.hasNutritionist),
              profile: result.profile,
              clientRowIds: result.clientRowIds || [],
              restrictionTags: result.profile?.restrictions || m.restrictionTags,
              restrictionLabels: result.profile?.restrictionLabels || m.restrictionLabels,
              email: result.profile?.email || m.email,
            }
          : m,
      ),
      customer: {
        ...prev.customer,
        name: result.profile?.name || prev.customer.name,
        email: result.profile?.email || prev.customer.email,
      },
    }));
  }, []);

  const addToCart = useCallback((dish, memberId) => {
    setState((prev) => {
      const mid = memberId || prev.activeMemberId;
      const lineId = cartLineId(mid, dish.dishId, dish.mealType);
      const existing = prev.cart.find((c) => c.lineId === lineId);
      if (existing) {
        return {
          ...prev,
          cart: prev.cart.map((c) =>
            c.lineId === lineId ? { ...c, quantity: c.quantity + 1 } : c,
          ),
        };
      }
      // First time this dish is added — allergy/diet auto-substitutions are
      // applied as the line is built (see utils/cartLine.js).
      const member = prev.householdMembers.find((m) => m.id === mid);
      return { ...prev, cart: [...prev.cart, buildCartLine(dish, mid, member)] };
    });
  }, []);

  /** Commits a line that was built and configured elsewhere — MenuPage holds a
   *  draft line while the customer customizes it in the modal, and only adds it
   *  here once they confirm. A line id already in the cart means the customer is
   *  adding a second serving of the same dish (or reassigned a draft to a member
   *  who already has it), so it becomes its own separately-customizable line
   *  rather than overwriting what's there. */
  const addCartLine = useCallback((line) => {
    setState((prev) => {
      const collision = prev.cart.some((c) => c.lineId === line.lineId);
      return { ...prev, cart: [...prev.cart, collision ? duplicateCartLine(line) : line] };
    });
  }, []);

  /** Set an exact quantity on a line; removes it at zero. */
  const setCartLineQuantity = useCallback((lineId, quantity) => {
    setState((prev) => ({
      ...prev,
      cart: prev.cart
        .map((c) => (c.lineId === lineId ? { ...c, quantity } : c))
        .filter((c) => (c.quantity || 0) > 0),
    }));
  }, []);

  /** Drop every cart line and remember which menu week the cart belongs to. */
  const resetCartForWeek = useCallback((deliveryWeek) => {
    setState((prev) => ({ ...prev, cart: [], cartWeek: deliveryWeek || null }));
  }, []);

  /** Drop every cart line, e.g. once a Stripe payment is confirmed. */
  const clearCart = useCallback(() => {
    setState((prev) => ({ ...prev, cart: [] }));
  }, []);

  /** Record the menu week the current cart was built from. */
  const setCartWeek = useCallback((deliveryWeek) => {
    setState((prev) => (prev.cartWeek === deliveryWeek ? prev : { ...prev, cartWeek: deliveryWeek || null }));
  }, []);

  const updateCartLine = useCallback((lineId, updates) => {
    setState((prev) => ({
      ...prev,
      cart: prev.cart
        .map((c) => (c.lineId === lineId ? { ...c, ...updates } : c))
        .filter((c) => c.quantity > 0),
    }));
  }, []);

  /** Schedules one line for a slot ({day, time, deliveryDate} — utils/deliverySlots.js). */
  const setCartLineDeliverySlot = useCallback((lineId, slot) => {
    const deliverySlot = slotKey(slot.day, slot.time);
    setState((prev) => ({
      ...prev,
      cart: prev.cart.map((c) => (c.lineId === lineId ? { ...c, deliverySlot, deliveryDate: slot.deliveryDate } : c)),
    }));
  }, []);

  /**
   * Assigns `defaultSlot` to every line not on a slot this zip serves. A line
   * already on a valid slot keeps it, but its `deliveryDate` is refreshed from
   * `validSlots` (covers an older cart saved before per-line dates existed).
   * @param {Array<{day,time,deliveryDate}>} validSlots - slots the current zip supports
   */
  const setCartDeliverySlots = useCallback((defaultSlot, { validSlots = [] } = {}) => {
    const byKey = new Map(validSlots.map((s) => [slotKey(s.day, s.time), s]));
    const deliverySlot = slotKey(defaultSlot.day, defaultSlot.time);
    setState((prev) => {
      let changed = false;
      const cart = prev.cart.map((c) => {
        const validSlot = byKey.get(c.deliverySlot);
        if (validSlot) {
          if (c.deliveryDate === validSlot.deliveryDate) return c;
          changed = true;
          return { ...c, deliveryDate: validSlot.deliveryDate };
        }
        changed = true;
        return { ...c, deliverySlot, deliveryDate: defaultSlot.deliveryDate };
      });
      return changed ? { ...prev, cart } : prev;
    });
  }, []);

  /**
   * Peels one serving off a line onto its own delivery slot. A single-serving
   * line has nothing to peel, so it just moves to the new slot.
   *
   * Peel and reschedule are one update, not a split plus a follow-up on the
   * returned id: state updaters may run async, so the caller can't rely on
   * knowing the new line's id.
   */
  const splitCartLineServing = useCallback((lineId, slot) => {
    const deliverySlot = slotKey(slot.day, slot.time);
    setState((prev) => {
      const line = prev.cart.find((c) => c.lineId === lineId);
      if (!line) return prev;
      if ((line.quantity || 1) <= 1) {
        return {
          ...prev,
          cart: prev.cart.map((c) => (c.lineId === lineId ? { ...c, deliverySlot, deliveryDate: slot.deliveryDate } : c)),
        };
      }
      return {
        ...prev,
        cart: [
          ...prev.cart.map((c) => (c.lineId === lineId ? { ...c, quantity: c.quantity - 1 } : c)),
          { ...duplicateCartLine(line), deliverySlot, deliveryDate: slot.deliveryDate },
        ],
      };
    });
  }, []);

  /**
   * Reassigns a cart line to a different household member. The member id is
   * baked into the lineId, so this re-keys the line rather than just setting a
   * field — otherwise the line's id would disagree with its memberId and the
   * modal would lose track of which line it is editing.
   * If the target member already has an identical line, the two are merged.
   * The ingredients are left exactly as the customer chose them; only the
   * previous member's substitution history is dropped, since it described their
   * allergies. Whether the meal conflicts for its new owner is derived where the
   * warning is shown, so nothing here needs re-flagging.
   * @returns {string|null} the line's new id, or null if it wasn't found.
   */
  const moveCartLineToMember = useCallback((lineId, memberId) => {
    let resultId = null;
    setState((prev) => {
      const line = prev.cart.find((c) => c.lineId === lineId);
      if (!line || line.memberId === memberId) return prev;

      const moved = clearAdjustmentHistory(relineForMember(line, memberId));
      const newLineId = moved.lineId;
      const collision = prev.cart.find((c) => c.lineId === newLineId);
      resultId = newLineId;

      if (collision) {
        return {
          ...prev,
          cart: prev.cart
            .filter((c) => c.lineId !== lineId)
            .map((c) => (c.lineId === newLineId
              ? { ...c, quantity: (c.quantity || 1) + (line.quantity || 1) }
              : c)),
        };
      }

      return {
        ...prev,
        cart: prev.cart.map((c) => (c.lineId === lineId ? moved : c)),
      };
    });
    return resultId;
  }, []);

  const removeFromCart = useCallback((lineId) => {
    setState((prev) => ({
      ...prev,
      cart: prev.cart.filter((c) => c.lineId !== lineId),
    }));
  }, []);

  const getCartQuantity = useCallback(
    (dishId, memberId) => {
      const mid = memberId || state.activeMemberId;
      const line = state.cart.find(
        (c) => c.dishId === dishId && c.memberId === mid,
      );
      return line?.quantity || 0;
    },
    [state.cart, state.activeMemberId],
  );

  const setDelivery = useCallback((delivery) => {
    setState((prev) => ({ ...prev, delivery: { ...prev.delivery, ...delivery } }));
  }, []);

  /** Reset delivery fields to blank, e.g. once a Stripe payment is confirmed. */
  const clearDelivery = useCallback(() => {
    setState((prev) => ({ ...prev, delivery: defaultDelivery() }));
  }, []);

  const setCustomer = useCallback((customer) => {
    setState((prev) => ({ ...prev, customer: { ...prev.customer, ...customer } }));
  }, []);

  const setLastOrder = useCallback((orderId) => {
    setState((prev) => ({ ...prev, lastOrderId: orderId }));
  }, []);

  const addPendingInvite = useCallback((memberId, email) => {
    setState((prev) => ({
      ...prev,
      pendingInvites: [
        ...(prev.pendingInvites || []).filter((i) => i.memberId !== memberId),
        { memberId, email, sentAt: new Date().toISOString() },
      ],
      householdMembers: prev.householdMembers.map((m) =>
        m.id === memberId ? { ...m, email } : m,
      ),
    }));
  }, []);

  const cartCount = useMemo(
    () => state.cart.reduce((n, line) => n + (line.quantity || 0), 0),
    [state.cart],
  );

  const cartSubtotal = useMemo(
    () => state.cart.reduce((sum, line) => sum + getLineUnitPrice(line) * (line.quantity || 1), 0),
    [state.cart],
  );

  const primaryHasFullQuiz = householdMembers[0]?.hasFullQuiz;

  const value = useMemo(
    () => ({
      ...state,
      householdMembers,
      activeMember,
      activeMemberId,
      primaryHasFullQuiz,
      cartCount,
      cartSubtotal,
      startQuizFlow,
      startMenuFlow,
      setActiveMember,
      addHouseholdMember,
      updateHouseholdMember,
      removeHouseholdMember,
      completeQuizForMember,
      addToCart,
      addCartLine,
      setCartLineQuantity,
      resetCartForWeek,
      clearCart,
      setCartWeek,
      updateCartLine,
      setCartLineDeliverySlot,
      setCartDeliverySlots,
      splitCartLineServing,
      moveCartLineToMember,
      removeFromCart,
      getCartQuantity,
      setDelivery,
      clearDelivery,
      setCustomer,
      setLastOrder,
      addPendingInvite,
    }),
    [
      state,
      householdMembers,
      activeMember,
      activeMemberId,
      primaryHasFullQuiz,
      cartCount,
      cartSubtotal,
      startQuizFlow,
      startMenuFlow,
      setActiveMember,
      addHouseholdMember,
      updateHouseholdMember,
      removeHouseholdMember,
      completeQuizForMember,
      addToCart,
      addCartLine,
      setCartLineQuantity,
      resetCartForWeek,
      clearCart,
      setCartWeek,
      updateCartLine,
      setCartLineDeliverySlot,
      setCartDeliverySlots,
      splitCartLineServing,
      moveCartLineToMember,
      removeFromCart,
      getCartQuantity,
      setDelivery,
      clearDelivery,
      setCustomer,
      setLastOrder,
      addPendingInvite,
    ],
  );

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}

export function useFlow() {
  const ctx = useContext(FlowContext);
  if (!ctx) throw new Error('useFlow must be used within FlowProvider');
  return ctx;
}
